import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import StudioCanvas from "./StudioCanvas";
import { textRuns } from "./studioMeasure";
import { getStyle, resolveText } from "./studioModel";
export function preflight(doc, content) {
  const issues = [];
  doc.pages.forEach((page) =>
    page.elements
      .filter((n) => !n.hidden)
      .forEach((node) => {
        const add = (message) =>
          issues.push({
            pageId: page.id,
            nodeId: node.id,
            name: node.name,
            message,
          });
        const radians = (node.rotation * Math.PI) / 180;
        const rw =
          Math.abs(node.w * Math.cos(radians)) +
          Math.abs(node.h * Math.sin(radians));
        const rh =
          Math.abs(node.w * Math.sin(radians)) +
          Math.abs(node.h * Math.cos(radians));
        const x = node.x + (node.w - rw) / 2,
          y = node.y + (node.h - rh) / 2;
        if (x < 0 || y < 0 || x + rw > page.width || y + rh > page.height)
          add("Extends beyond the paper edge");
        if (node.type === "text") {
          const style = getStyle(doc, node),
            text = String(resolveText(node, content) ?? "");
          // Same runs the canvas and every export draw (shared core).
          const { runs, original } = textRuns(node, style, text);
          if (!original && runs.length && (runs.length - 1) * style.fontSize * style.lineHeight + style.fontSize > node.h + 1)
            add(
              "Text overflows its frame — enlarge the frame or reduce type size"
            );
          if (style.fontSize < 7)
            add("Small text may be difficult to read in print");
          if (
            node.binding &&
            content[node.binding] === undefined &&
            node.textOverride === undefined
          )
            add("CMS source was removed — displaying last stored copy");
        }
      })
  );
  return issues;
}
export function svgMarkup(doc, page, content) {
  return renderToStaticMarkup(
    <StudioCanvas doc={doc} page={page} content={content} preview />
  );
}
// Saving files. Where the browser supports it (Chrome, Edge), a Save dialog
// lets the user choose the folder and file name; elsewhere the browser's own
// download setting decides where the file goes.
const TYPES = {
  "application/pdf": { description: "PDF document", ext: ".pdf" },
  "application/json": { description: "JSON file", ext: ".json" },
  "image/svg+xml": { description: "SVG image", ext: ".svg" },
  "image/png": { description: "PNG image", ext: ".png" },
};
export function fileNameWithExtension(name, ext) {
  const clean = String(name || "").replace(/[\\/:*?"<>|]/g, "-").replace(/\s+/g, " ").trim() || "menu";
  return clean.toLowerCase().endsWith(ext) ? clean : `${clean}${ext}`;
}
export function canChooseSaveLocation() {
  return typeof window !== "undefined" && typeof window.showSaveFilePicker === "function";
}
// Must run directly from a click, before any slow work. Returns a file handle,
// null if the user cancelled, or undefined when the dialog is unavailable.
export async function chooseSaveLocation(suggestedName, mime) {
  if (!canChooseSaveLocation()) return undefined;
  const type = TYPES[mime];
  try {
    return await window.showSaveFilePicker({ suggestedName, types: type ? [{ description: type.description, accept: { [mime]: [type.ext] } }] : undefined });
  } catch (err) {
    if (err && err.name === "AbortError") return null;
    return undefined;
  }
}
export async function saveFile(blob, filename, handle) {
  if (handle) {
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return true;
  }
  anchorDownload(blob, filename);
  return true;
}
// Save a ready-made file: Save dialog where available, otherwise a download.
export async function download(blob, filename) {
  const handle = await chooseSaveLocation(filename, blob.type);
  if (handle === null) return false;
  return saveFile(blob, filename, handle);
}
function anchorDownload(blob, filename) {
  const url = URL.createObjectURL(blob),
    link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
// Measure the same SVG text used by the canvas and PDF, including original runs.
// Frame rectangles are deliberately excluded: their padding is not printed ink.
export function textLayoutReport(doc, content) {
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-10000px;top:0;visibility:hidden";
  document.body.appendChild(host);
  try {
    return doc.pages.map((page) => {
      host.innerHTML = svgMarkup(doc, page, content);
      const nodes = page.elements.filter((n) => n.type === "text" && !n.hidden && n.opacity !== 0).map((node) => {
        const text = host.querySelector(`[data-node-id="${CSS.escape(node.id)}"] text`);
        const box = text.getBBox();
        const pageBounds = (r) => {
          const angle = (node.rotation || 0) * Math.PI / 180, cx = node.w / 2, cy = node.h / 2;
          const points = [[r.x, r.y], [r.x + r.width, r.y], [r.x, r.y + r.height], [r.x + r.width, r.y + r.height]].map(([x, y]) => ({
            x: node.x + cx + (x - cx) * Math.cos(angle) - (y - cy) * Math.sin(angle),
            y: node.y + cy + (x - cx) * Math.sin(angle) + (y - cy) * Math.cos(angle),
          }));
          const x = Math.min(...points.map((p) => p.x)), y = Math.min(...points.map((p) => p.y));
          return { x, y, w: Math.max(...points.map((p) => p.x)) - x, h: Math.max(...points.map((p) => p.y)) - y };
        };
        const runs = Array.from(text.querySelectorAll("tspan"), (span) => {
          return pageBounds(span.getBBox());
        });
        return { id: node.id, text: resolveText(node, content), ...pageBounds(box), runs };
      });
      const overlaps = [];
      nodes.forEach((a, i) => nodes.slice(i + 1).forEach((b) => {
        if (a.runs.some((r) => b.runs.some((s) => Math.min(r.x + r.w, s.x + s.w) - Math.max(r.x, s.x) > 0.5 && Math.min(r.y + r.h, s.y + s.h) - Math.max(r.y, s.y) > 0.5))) overlaps.push([a.id, b.id]);
      }));
      return { id: page.id, name: page.name, nodes, overlaps };
    });
  } finally { host.remove(); }
}
export function exportSVG(doc, page, content) {
  download(
    new Blob([svgMarkup(doc, page, content)], { type: "image/svg+xml" }),
    `${doc.name}-${page.name}.svg`
  );
}
export async function exportPNG(doc, page, content, dpi = 300) {
  const svg = svgMarkup(doc, page, content),
    image = new Image();
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  try {
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round((page.width * dpi) / 72);
    canvas.height = Math.round((page.height * dpi) / 72);
    if (canvas.width * canvas.height > 80000000)
      throw new Error(
        "Page is too large for this resolution. Use SVG or reduce the page size."
      );
    const context = canvas.getContext("2d");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png")
    );
    if (!blob) throw new Error("Unable to create image. Try SVG export.");
    download(blob, `${doc.name}-${page.name}-${dpi}dpi.png`);
  } finally {
    URL.revokeObjectURL(url);
  }
}
export function printDocumentMarkup(doc, pages, content, title = "Trattoria Demi — Menu") {
  if (!pages.length) throw new Error("Select at least one page to export.");
  // Unnamed @page sets the print dialog default. Named pages alone can leave
  // the browser's previous Letter/A4 paper selection in place.
  const defaultPage = `@page{size:${pages[0].width}pt ${pages[0].height}pt;margin:0}`;
  const styles = defaultPage + pages
    .map(
      (p, i) =>
        `@page sheet${i}{size:${p.width}pt ${p.height}pt;margin:0}.sheet-${i}{page:sheet${i};width:${p.width}pt;height:${p.height}pt}.sheet-${i} svg{width:${p.width}pt;height:${p.height}pt}`
    )
    .join("");
  return `<!doctype html><html><head><title>${String(title).replace(/[<&]/g, "")}</title><style>*{box-sizing:border-box}body{margin:0}section{break-after:page;overflow:hidden}section:last-child{break-after:auto}svg{display:block}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}${styles}</style></head><body>${pages
    .map(
      (p, i) =>
        `<section class="sheet-${i}">${svgMarkup(doc, p, content)}</section>`
    )
    .join("")}</body></html>`;
 }
// The print dialog's "Save as PDF" suggests the document title as file name.
export async function printDocument(doc, pages, content, { fileName } = {}) {
  document.getElementById("menu-studio-print-frame")?.remove();
  const frame = document.createElement("iframe");
  frame.id = "menu-studio-print-frame";
  frame.title = "Menu print preview";
  frame.style.cssText =
    "position:fixed;width:1px;height:1px;right:0;bottom:0;border:0;opacity:0;pointer-events:none";
  const loaded = new Promise((resolve) => {
    frame.onload = resolve;
  });
  frame.srcdoc = printDocumentMarkup(doc, pages, content, fileName ? fileName.replace(/\.pdf$/i, "") : undefined);
  document.body.appendChild(frame);
  await loaded;
  await frame.contentDocument.fonts.ready;
  await Promise.all(
    Array.from(frame.contentDocument.images).map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          })
    )
  );
  frame.contentWindow.focus();
  frame.contentWindow.print();
}

// Render with the Studio's fonts and SVG layout at print resolution. The PDF
// stores these exact sheets, without print-dialog scaling or paper rotation.
export async function exportPDF(doc, pages, content, { fileName, handle } = {}) {
  if (!pages.length) throw new Error("Select at least one page to export.");
  await document.fonts?.ready;
  const { createMenuPDF } = await import("./studioPdf");
  const pdf = await createMenuPDF(pages, async page => {
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(page.width * 300 / 72);
    canvas.height = Math.round(page.height * 300 / 72);
    if (canvas.width * canvas.height > 80000000) throw new Error("Page is too large to export at print resolution.");
    const image = new Image();
    const url = URL.createObjectURL(new Blob([svgMarkup(doc, page, content)], { type: "image/svg+xml" }));
    try {
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error("Could not render the menu page. Please retry."));
        image.src = url;
      });
      const context = canvas.getContext("2d");
      context.fillStyle = page.background || "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL("image/png");
    } finally { URL.revokeObjectURL(url); }
  });
  await saveFile(pdf.output("blob"), fileNameWithExtension(fileName || doc.name, ".pdf"), handle);
}

// Vector PDF (phase 5): the browser prints the same SVG sheets, so text stays
// selectable and sharp. It is offered only when a bounded check passes;
// otherwise the print-resolution image PDF remains the export.
const GENERIC = /^(serif|sans-serif|cursive|fantasy|monospace|system-ui)$/i;
export function vectorExportCheck(doc, pages, env = typeof window !== "undefined" ? window : {}) {
  const issues = [];
  const embedded = new Set((doc.fonts || []).map((f) => f.family.toLowerCase()));
  const families = new Set();
  pages.forEach((page) => page.elements.forEach((n) => {
    if (n.type !== "text" || n.hidden) return;
    const first = String(getStyle(doc, n).fontFamily || "").split(",")[0].replace(/["']/g, "").trim();
    if (first) families.add(first);
  }));
  for (const family of families) {
    if (embedded.has(family.toLowerCase()) || GENERIC.test(family)) continue;
    const local = env.document?.fonts?.check?.(`12px "${family}"`);
    if (!local) issues.push(`${family} is neither embedded nor installed on this computer.`);
  }
  const sizes = new Set(pages.map((p) => `${p.width}x${p.height}`));
  const ua = env.navigator?.userAgent || "";
  if (sizes.size > 1 && /Firefox/.test(ua)) issues.push("This browser prints mixed page sizes at one size.");
  if (!/Chrome|Chromium|Edg|Safari|Firefox/.test(ua)) issues.push("This browser has not been checked for vector printing.");
  return { ok: issues.length === 0, issues, families: [...families] };
}
